import React, { FC, useState } from 'react';
import {
	Box,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Grid,
	IconButton,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import {
	DataGrid,
	GridColDef,
	GridToolbarQuickFilter,
	GridToolbarContainer,
} from '@mui/x-data-grid';
import { ProviderCaseQueue } from 'type-declarations';
import './styles.scss';

type Props = {
	isLoading: boolean;
	headers: GridColDef[];
	data: ProviderCaseQueue[];
	addBtnLabel?: string;
	isEditable: boolean;
	editDialogTitle?: string;
	deleteDialogTitle?: string;
	CreateAndEditDialogComponent: React.FC<any>;
	baseRecord: unknown;
	// Actions are props passed into the <SimpleCrudTable /> & invoked here or in <CreateAndEditDialogComponent />
	createAction: (...any) => void;
	editAction: (...any) => void;
	deleteAction: (...any) => void;
};

export const SimpleCrudTable: FC<Props> = ({
	isLoading,
	headers,
	data,
	addBtnLabel,
	isEditable,
	editDialogTitle,
	deleteDialogTitle,
	CreateAndEditDialogComponent,
	baseRecord,
	// Actions are props passed into the <SimpleCrudTable /> & invoked here or in <CreateAndEditDialogComponent />
	createAction,
	editAction,
	deleteAction,
}) => {
	const [showEditDialog, setShowEditDialog] = useState(false);
	const [showDeleteRecordDialog, setShowDeleteRecordDialog] = useState(false);
	const [selectedEditRowRecord, setSelectedEditRowRecord] = useState<any | null>(baseRecord);
	const [selectedDeleteRowId, setSelectedDeleteRowId] = useState<string | null>(null);

	// Action column for edit/delete, shows when prop 'isEditable' is true
	const actionsColumn = {
		field: 'actions',
		headerName: '',
		sortable: false,
		filterable: false,
		renderCell: (params): React.ReactNode => (
			<>
				<IconButton
					onClick={() => {
						setSelectedEditRowRecord(data.find((schedule) => schedule.id === params.row.id));
						setShowEditDialog(true);
					}}
				>
					<EditIcon sx={{ color: 'var(--rhyme-light-btn-blue)' }} />
				</IconButton>
				<IconButton
					onClick={() => {
						setSelectedDeleteRowId(params.row.id);
						setShowDeleteRecordDialog(true);
					}}
				>
					<DeleteIcon sx={{ color: 'var(--rhyme-accessibility-error)' }} />
				</IconButton>
			</>
		),
		width: 100,
	};

	// Simple delete dialog with default & custom message support
	const ConfirmDeleteRecordDialog = (): React.ReactNode => (
		<>
			<Dialog open={showDeleteRecordDialog} className="simple-crud-table-dialog">
				<DialogTitle sx={{ textAlign: 'center' }}>
					{deleteDialogTitle ?? 'Remove Record?'}
				</DialogTitle>
				<DialogContent>
					<DialogActions sx={{ justifyContent: 'center' }}>
						<Button onClick={() => setShowDeleteRecordDialog(false)}>Cancel</Button>
						<Button
							type="submit"
							onClick={(id) => {
								deleteAction(selectedDeleteRowId);
								setSelectedDeleteRowId(null);
								setShowDeleteRecordDialog(false);
							}}
							sx={{ backgroundColor: 'var(--rhyme-accessibility-error)' }}
						>
							Delete
						</Button>
					</DialogActions>
				</DialogContent>
			</Dialog>
		</>
	);

	const customToolbar = (): React.ReactNode => {
		return (
			<GridToolbarContainer sx={{ margin: 1 }}>
				<Grid container sx={{ width: '100%' }} spacing={2}>
					<Grid size={2}>
						<Button sx={{ height: '55px', width: '100%' }} onClick={() => setShowEditDialog(true)}>
							{addBtnLabel || 'Add Record'}
						</Button>
					</Grid>
					<Grid size={10}>
						<GridToolbarQuickFilter
							disabled={isLoading}
							variant="outlined"
							sx={{ width: '100%' }}
						/>
					</Grid>
				</Grid>
			</GridToolbarContainer>
		);
	};

	return (
		<Box className="simple-crud-table">
			<DataGrid
				disableMultipleRowSelection
				loading={isLoading}
				columns={isEditable ? [actionsColumn, ...headers] : headers}
				rows={data}
				slots={{ toolbar: customToolbar }}
			/>
			<CreateAndEditDialogComponent
				open={showEditDialog}
				dialogTitle={editDialogTitle}
				selectedEditRowRecord={selectedEditRowRecord}
				createAction={(evt): void => {
					createAction(evt);
					setSelectedEditRowRecord(null);
					setShowEditDialog(false);
				}}
				editAction={(evt, id): void => {
					editAction(evt, id);
					setSelectedEditRowRecord(null);
					setShowEditDialog(false);
				}}
				cancelAction={(): void => {
					setSelectedEditRowRecord(null);
					setShowEditDialog(false);
				}}
			/>
			<ConfirmDeleteRecordDialog />
		</Box>
	);
};
